/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

import axios from "axios";
import Oidc, { UserManager, User, WebStorageStateStore } from "oidc-client";
import React, {
	createContext,
	useState,
	useEffect,
	useContext,
	useCallback,
	useMemo,
	useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useSWR, { useSWRConfig } from "swr";
import { navigate } from "gatsby";

export interface Business {
	id: string;
	name: string;
	displayName: string;
	logoSrc: string;
	createdAt: Date;
	defaultCampaignId: string;
}

interface ProviderContext<T> {
	value: T | null | undefined
	setValue(v: T | null | undefined): void
  }
  
  const BusinessContext = createContext<
	ProviderContext<Business> | null | undefined
  >(undefined)
  export const useBusiness = () => useContext(BusinessContext)?.value

let accessToken = "";
export const getAccessToken = () => accessToken;

class SilentRenewService {
	/* eslint-disable no-underscore-dangle */
	private cb: any;
	private mgr: UserManager
	private renewKey: string

	constructor(mgr: UserManager) {
		this.mgr = mgr
		this.renewKey = `oidc.renew:${this.mgr.settings.authority}:${this.mgr.settings.client_id}`
	}

	start() {
		if (!this.cb) {
			this.cb = this._tokenExpiring.bind(this);
			this.mgr.events.addAccessTokenExpiring(this.cb);

			// Load the user so the expiring events can be initialized.
			this.mgr.getUser().catch((err: Error) => {
				// catch to suppress errors since we're in a ctor.
			});
		}
	}

	stop() {
		if (!this.cb) return;
		this.mgr.events.removeAccessTokenExpiring(this.cb);
		delete this.cb;
	}

	// private renewKey = `oidc.renew:${this.mgr.settings.authority}:${this.mgr.settings.client_id}`;

	/* eslint-disable class-methods-use-this */

	setIsSilentRenewing() {
		window.localStorage.setItem(this.renewKey, new Date().getTime().toString());
	}

	clearIsSilentRenewing() {
		window.localStorage.removeItem(this.renewKey);
	}

	/* eslint-enable class-methods-use-this */

	checkIfSilentRenewing() {
		const renewTime = parseInt(
			window.localStorage.getItem(this.renewKey) || "0",
			10
		);

		if (new Date().getTime() > renewTime + 30000) {
			this.clearIsSilentRenewing();
			return false;
		}

		return true;
	}

	waitIfAlreadyRenewing() {
		return new Promise<void>((resolve) => {
			const check = () =>
				// eslint-disable-next-line @typescript-eslint/no-shadow
				new Promise<void>((resolve) => {
					if (this.checkIfSilentRenewing()) {
						setTimeout(() => check().then(resolve), 5000);
					} else {
						resolve();
					}
				});

			check().then(resolve);
		});
	}

	_tokenExpiring() {
		this.waitIfAlreadyRenewing().then(() => {
			// Recheck the token to see if its valid
			(this.mgr as any)._loadUser().then((user: Oidc.User) => {
				// If the token expires more than 1 min in the future, assume
				// its been renewed by another process
				if (user.expires_at * 1000 > new Date().getTime() + 60 * 1000) {
					(this.mgr as any)._events.load(user);
				} else if (user.expires_at * 1000 < new Date().getTime()) {
					// If the token is already expired then do nothing - the
					// 'Access token expired' timer can handle it
				} else {
					// Mark this session as renewing the token
					this.setIsSilentRenewing();

					this.mgr.signinSilent().then(
						() => {
							this.clearIsSilentRenewing();
						},
						(err: Error) => {
							(this.mgr as any).events._raiseSilentRenewError(err);
							this.clearIsSilentRenewing();
						}
					);
				}
			});
		});
	}
	/* eslint-enable no-underscore-dangle */
}

export const Context = createContext<any>(undefined);

interface AuthProviderProps {
	// NOTE: The following props are not expected to ever change.
	authority: string;
	clientId: string;
	redirectUri: string;
	postLogoutRedirectUri: string;
}

export const AuthProvider: React.FC<AuthProviderProps> = (props) => {
	const [user, setUser] = useState<Oidc.User | null | "loading">("loading");
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	const loc = useLocation();

	const mgr = useRef(
		new UserManager({
			userStore: new WebStorageStateStore({ store: window.localStorage }),
			automaticSilentRenew: false,
			response_type: "code",
			response_mode: "query",
			prompt: "consent",
			scope: "openid email profile offline_access",
			authority: props.authority,
			client_id: props.clientId,
			redirect_uri: props.redirectUri,
			post_logout_redirect_uri: props.postLogoutRedirectUri,
		})
	);

	const auth = useAuth(mgr.current);
	const renewer = useRef(new SilentRenewService(mgr.current));

	const onUserChanged = useCallback(
		(u?: Oidc.User | null) => {
			if (u?.expired === true) return;
			accessToken = u?.access_token ?? "";
			setUser(u ?? null);
		},
		[setUser]
	);

	const onExpired = useCallback(async () => {
		try {
			const u = await mgr.current.signinSilent();
			setUser(u);
		} catch (e) {
			if (e instanceof Error && e.message === "Network Error") {
				// eslint-disable-next-line no-alert
				alert(
					"We are having trouble verifying your login. You might not be connected to the Internet. Please try reconnecting and refreshing this page."
				);
			} else {
				// Report unexpected errors.
				// "invalid_grant" is probably an expired refresh token.
				if (e instanceof Error && e.message !== "invalid_grant")
					// Sentry.captureError(e);

				// Best way to recover is to re-auth and redirect to same page.
				localStorage.setItem("auth-return-to", loc.pathname);
				auth();
			}
		}
	}, [auth, loc]);

	useEffect(() => {
		const m = mgr.current;
		m.events.addUserLoaded(onUserChanged);
		return () => m.events.removeUserLoaded(onUserChanged);
	}, [onUserChanged, mgr]);

	useEffect(() => {
		const m = mgr.current;
		m.events.addUserUnloaded(onUserChanged);
		return () => m.events.removeUserUnloaded(onUserChanged);
	}, [onUserChanged, mgr]);

	useEffect(() => {
		const m = mgr.current;
		m.events.addAccessTokenExpired(onExpired);
		return () => m.events.removeAccessTokenExpired(onExpired);
	}, [onExpired, mgr]);

	useEffect(() => {
		const m = mgr.current;
		m.events.addSilentRenewError(onExpired);
		return () => m.events.removeSilentRenewError(onExpired);
	}, [onExpired, mgr]);

	useEffect(() => {
		renewer.current.start();

		(async () => {
			const u = await mgr.current.getUser();
			if (u?.expired !== true) {
				accessToken = u?.access_token ?? "";
				setUser(u);
			}
		})();
	}, [renewer, mgr]);

	const value = useMemo(
		() => ({ mgr, user, setUser }),
		[mgr, user, setUser]
	);
	return <Context.Provider value={value}>{props.children}</Context.Provider>;
};

const useCtx = () => {
	const ctx = useContext(Context);
	if (!ctx) throw new Error("Called outside of context");
	return ctx;
};

const useMgr = () => useCtx().mgr.current;
export const useLogout = () => {
	const mgr = useMgr();
	return useCallback(async () => {
		const u = await mgr.getUser();

		if (u?.id_token_hint || u?.id_token) {
			return await mgr.signoutRedirect(); // Authed via PKCE
		} else {
			await mgr.removeUser(); // Authed via token grant
			const returnTo = localStorage.getItem("auth-return-to");
			localStorage.removeItem("auth-return-to");
			// NOTE: It's important to use browser native navigation instead of
			// a React Router API. The page needs to "refresh" to match the
			// behavior of a signout redirect (for userse authed via PKCE).
			if (returnTo) window.location.pathname = returnTo;
		}
	}, [mgr]);
};
export const useUser = () => useCtx().user;
export const useUserId = () => useUser()?.profile?.sub ?? "";

/**
 * Returns a bool if the user is authed or unauthed, or null if loading.
 * The return value can be treated as truthy or falsy, or checked explicitly if
 * it is needed to distinguish between unauthed and loading.
 */
export const useIsAuthed = () => {
	const u = useUser();
	const now = Math.floor(new Date().getTime() / 1000); // unix time

	if (u === "loading" || u?.expires_at < now) return null;
	return u !== null;
};

/** Returns a function which can be used to trigger an OIDC login popup. */
export const useAuth = (mgr?: UserManager) => {
	const fromCtx = useContext(Context)?.mgr.current;
	mgr = mgr ?? fromCtx;
	if (!mgr) throw new Error("No UserManager");

	return useCallback(
		async (q: Record<string, string> = {}) => {
			// ref: https://github.com/IdentityModel/oidc-client-js/blob/2e5e28b2f531141616463cb91ec632115216b650/src/UserManager.js#L89

			// eslint-disable-next-line no-underscore-dangle
			const navigator = (mgr as any)._redirectNavigator;
			const navigatorParams: any = {};

			// ref: https://github.com/IdentityModel/oidc-client-js/blob/2e5e28b2f531141616463cb91ec632115216b650/src/UserManager.js#L379

			const handle = await navigator.prepare(navigatorParams);

			try {
				const req = await mgr!.createSigninRequest({ request_type: "si:r" });
				const url = new URL(req.url);

				Object.entries(q).forEach(([key, value]) => {
					if (value !== undefined) url.searchParams.set(key, value);
				});

				navigatorParams.url = url.toString();
				navigatorParams.id = req.state.id;

				return handle.navigate(navigatorParams);
			} catch (e) {
				handle.close?.();
				throw e;
			}
		},
		[mgr]
	);
};

export const useGrant = () => {
	const mgr = useMgr();

	return useCallback(
		async (gty: string, reqData: any, store: boolean = true) => {
			const { data } = await axios.request({
				method: "post",
				url: "/token",
				baseURL: mgr.settings.authority,
				data: new URLSearchParams({
					grant_type: gty,
					client_id: mgr.settings.client_id,
					...reqData,
				}),
			});

			if (store) {
				const u = new User(data);
				// `expires_in` must be explicitly set, or else it's missing.
				if (data.expires_in) (u as any).expires_in = data.expires_in;

				if (mgr.settings.loadUserInfo) {
					const meRes = await axios.get("/me", {
						baseURL: mgr.settings.authority,
						headers: { authorization: `Bearer ${data.access_token}` },
					});
					u.profile = meRes.data;
				}

				await mgr.storeUser(u);
				mgr.events.load(u);
			}

			return data;
		},
		[mgr]
	);
};

export const useUpdateUser = () => {
	const mgr = useMgr();

	return useCallback(
		async (data: any) => {
			const res = await axios.patch("/api/me", data, {
				baseURL: mgr.settings.authority,
				headers: { authorization: `Bearer ${accessToken}` },
			});

			const u = await mgr.getUser();
			u.profile = { ...u.profile, ...res.data };
			await mgr.storeUser(u);
			mgr.events.load(u);
		},
		[mgr],
	);
}

export const InCallback: React.FC = ({ children }) => {
	const mgr = useMgr();
	const h = useNavigate();

	const onLoaded = useCallback(() => {
		mgr.clearStaleState();
		navigate(localStorage.getItem('auth-return-to'));
		localStorage.removeItem("auth-return-to");
	}, [mgr, h]);

	useEffect(() => {
		(async () => {
			try {
				await mgr.signinCallback();
			} catch (e) {
				if (
					e instanceof Error &&
					e.message === "No matching state found in storage"
				)
					h("/app/login");
				else throw e;
			}
		})();
	}, [mgr, h]);

	useEffect(() => {
		mgr.events.addUserLoaded(onLoaded);
		return () => mgr.events.removeUserLoaded(onLoaded);
	}, [mgr, onLoaded]);

	// eslint-disable-next-line react/jsx-no-useless-fragment
	return <>{children}</>;
};

export const OutCallback: React.FC = ({ children }) => {
	const mgr = useMgr();
	const h = useNavigate();

	const onUnloaded = useCallback(() => {
		mgr.clearStaleState();
		const path = localStorage.getItem("auth-return-to") ?? "/";
		localStorage.removeItem("auth-return-to");
		navigate('/general/overview/home');
	}, [mgr, h]);

	useEffect(() => {
		(async () => {
			await mgr.signoutCallback();
			localStorage.removeItem("ui.recent-users")
			onUnloaded();
		})();
	}, [mgr, onUnloaded]);

	// eslint-disable-next-line react/jsx-no-useless-fragment
	return <>{children}</>;
};

export const Login: React.FC = () => {
	const isAuthed = useIsAuthed();
	const auth = useAuth();
	const loc = useLocation();
	const q = new URLSearchParams(loc.search);
	const force = q.get("force");

	useEffect(() => {
		if (isAuthed === false || force) {
			const q = new URLSearchParams(loc.search);

			const p = Object.fromEntries(q.entries());
			p["anonymous-id"] = (window.analytics?.user?.() as any)?.anonymousId?.();
			const analyticsAuthExt = sessionStorage.getItem("context");
			if (analyticsAuthExt) p["analytics-auth-ext"] = analyticsAuthExt;

			auth(p);
		} else if (isAuthed === true) {
			navigate('/general/overview/home');
		}
	}, [isAuthed, auth, loc.search, force]);

	// return <Redirect to="/" />;
	return null;
};

export const Logout: React.FC = () => {
	const logout = useLogout();
	useEffect(() => {
		logout();
	}, [logout]);
	return null;
};

export const useCampaignUserInfo = (businessId?: string, campaignId?: string) => {
	const mgr = useMgr();
	const u = useUser();
	const business = useBusiness();

	if (!businessId)
		businessId = business?.id

	if (!campaignId)
		campaignId = business?.defaultCampaignId

	const key = `userInfo:${u.profile.sub}:${businessId}:${campaignId}`;

	const url = `/api/me/info?business-id=${businessId}&campaign-id=${campaignId}`;

	const { data, isValidating } = useSWR(key, async () => {
		const { data } = await axios.get(url, {
			baseURL: mgr.settings.authority,
			headers: { authorization: `Bearer ${accessToken}` },
		});

		return data;
	});

	return { data, isValidating };
};

export const useUpdateUserInfo = (ctx?: any) => {
	const mgr = useMgr();
	const u = useUser();
	const { mutate } = useSWRConfig();

	return useCallback(
		async (data: any, ctx2: any = ctx) => {
			await axios.patch("/api/me/info", { ...ctx2, data }, {
				baseURL: mgr.settings.authority,
				headers: { authorization: `Bearer ${accessToken}` },
			});

			if (ctx.businessId && ctx.campaignId) {
				mutate(`userInfo:${u.profile.sub}:${ctx.businessId}:${ctx.campaignId}`);
			}
		},
		[mgr, u, mutate, ctx],
	);
};
